import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import CloseIcon from '@mui/icons-material/Close'
import {
  Box,
  Button,
  IconButton,
  LinearProgress,
  Typography
} from '@mui/material'
import React from 'react'

import Database from '../../types/Database'

interface WizardStepContainerProps {
  children: React.ReactNode
  currentStep: Database.OnboardingStep
  totalSteps: number
  onBack?: () => void
  onSkip?: () => void
  showProgress?: boolean
  showBackButton?: boolean
  showSkipButton?: boolean
}

/**
 * WizardStepContainer - Layout wrapper for onboarding wizard steps
 *
 * Provides consistent layout with progress indicator, back button, and skip button.
 * Follows Material-UI theme patterns and ensures responsive design for mobile.
 */
const WizardStepContainer: React.FC<WizardStepContainerProps> = ({
  children,
  currentStep,
  totalSteps,
  onBack,
  onSkip,
  showProgress = true,
  showBackButton = true,
  showSkipButton = true
}) => {
  // Map steps to numeric values for progress calculation
  const stepOrder: Database.OnboardingStep[] = [
    'not_started',
    'office_choice',
    'join_instructions',
    'create_office',
    'desk_prompt',
    'create_desk',
    'complete'
  ]

  const currentStepIndex = stepOrder.indexOf(currentStep)
  const progressValue = ((currentStepIndex + 1) / totalSteps) * 100

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'background.default'
      }}
    >
      {/* Header with navigation controls */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: { xs: 2, sm: 4 },
          py: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          backgroundColor: 'background.paper'
        }}
      >
        {/* Back button */}
        <Box sx={{ minWidth: 100 }}>
          {showBackButton && onBack && (
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={onBack}
              sx={{
                textTransform: 'none',
                color: 'text.secondary',
                '&:hover': {
                  backgroundColor: 'action.hover'
                }
              }}
            >
              <Typography
                sx={{
                  display: { xs: 'none', sm: 'inline' }
                }}
              >
                Back
              </Typography>
            </Button>
          )}
        </Box>

        {/* Progress indicator */}
        {showProgress && (
          <Box
            sx={{
              flex: 1,
              mx: { xs: 2, sm: 4 },
              maxWidth: 400
            }}
          >
            <Typography
              variant='caption'
              sx={{
                display: 'block',
                textAlign: 'center',
                mb: 1,
                color: 'text.secondary',
                fontWeight: 500
              }}
            >
              Step {currentStepIndex + 1} of {totalSteps}
            </Typography>
            <LinearProgress
              variant='determinate'
              value={progressValue}
              sx={{
                height: 6,
                borderRadius: 3,
                backgroundColor: 'action.hover',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 3,
                  backgroundColor: 'primary.main'
                }
              }}
            />
          </Box>
        )}

        {/* Skip/Exit button */}
        <Box
          sx={{ minWidth: 100, display: 'flex', justifyContent: 'flex-end' }}
        >
          {showSkipButton && onSkip && (
            <IconButton
              id='close-wizard'
              onClick={onSkip}
              sx={{
                color: 'text.secondary',
                '&:hover': {
                  backgroundColor: 'action.hover'
                }
              }}
              aria-label='Skip or exit wizard'
            >
              <CloseIcon />
            </IconButton>
          )}
        </Box>
      </Box>

      {/* Main content area */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          px: { xs: 2, sm: 4 },
          py: { xs: 3, sm: 6 }
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: 800,
            mx: 'auto'
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  )
}

export default WizardStepContainer
